import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, PenLine, Type } from "lucide-react";
import { api } from "../lib/api";
import { optionalBio } from "../lib/passkey";
import { SignaturePad } from "./SignaturePad";

// Mandatory one-time e-signature undertaking for the rider/employee portal.
// Rebuilt fresh and foolproof on every device:
//  - Draw mode uses the proven SignaturePad (pointer events + touch-action:none) that works on touch.
//  - "Type name" fallback renders the name as a cursive signature onto a canvas for devices where drawing is awkward.
//  - A single, large submit button is pinned to the bottom of the screen so it can NEVER be cut off.
export const SignatureConsentGate = ({ onCleared }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("draw"); // "draw" | "type"
  const [typedName, setTypedName] = useState("");
  const sigRef = useRef(null);
  const typedCanvasRef = useRef(null);

  useEffect(() => {
    api.portalSignatureConsentStatus()
      .then((d) => {
        setData(d);
        if (d.status === "ok") onCleared?.();
        else setTypedName(d.name || "");
      })
      .catch(() => { setData({ status: "ok" }); onCleared?.(); })
      .finally(() => setLoading(false));
  }, [onCleared]);

  const renderTyped = useCallback((value) => {
    const c = typedCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    const n = (value || "").trim();
    if (!n) return;
    ctx.fillStyle = "#0f172a";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    let size = 72;
    ctx.font = `italic ${size}px 'Brush Script MT','Segoe Script','Snell Roundhand',cursive`;
    while (ctx.measureText(n).width > c.width - 48 && size > 22) {
      size -= 4;
      ctx.font = `italic ${size}px 'Brush Script MT','Segoe Script','Snell Roundhand',cursive`;
    }
    ctx.fillText(n, c.width / 2, c.height / 2);
  }, []);

  useEffect(() => {
    if (mode === "type" && data && data.status !== "ok") renderTyped(typedName);
  }, [typedName, mode, data, renderTyped]);

  const submit = async () => {
    let sig = null;
    if (mode === "draw") {
      if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("وقّع في المربّع أولاً"); return; }
      sig = sigRef.current.getSignature();
    } else {
      if (!typedName.trim()) { toast.error("اكتب اسمك أولاً"); return; }
      renderTyped(typedName);
      sig = typedCanvasRef.current.toDataURL("image/png");
    }
    if (!sig) { toast.error("تعذّر قراءة التوقيع، حاول مرة أخرى"); return; }
    setSubmitting(true);
    try {
      await api.portalSignatureConsentAccept(sig, await optionalBio("signature_consent"));
      toast.success("تم حفظ تعهّدك بنجاح ✅");
      setData({ status: "ok" });
      onCleared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحفظ، حاول مرة أخرى");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;
  if (!data || data.status === "ok") return null;

  return (
    <div className="fixed inset-0 z-[95] flex flex-col bg-slate-900/95" dir="rtl" data-testid="signature-consent-gate">
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="mx-auto my-2 max-w-lg rounded-3xl bg-white p-5 space-y-4 border-4 border-blue-500">
          <div className="text-center space-y-2">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100"><ShieldCheck className="h-8 w-8 text-blue-600" /></div>
            <h2 className="font-heading text-lg font-extrabold text-slate-900">تعهّد وإقرار بالتوقيع الإلكتروني</h2>
            <p className="text-xs text-slate-400">هذا التعهّد إلزامي للمتابعة ويُسجَّل لمرة واحدة</p>
          </div>

          <div className="rounded-xl border-2 border-blue-200 bg-blue-50/60 px-4 py-3">
            <p className="text-slate-700 leading-relaxed text-sm whitespace-pre-wrap" data-testid="consent-text">
              {data.text || "أقرّ وأتعهّد بأن التوقيع المسجّل باسمي في هذا الموقع يعود لي شخصياً وأنه توقيعي المعتمد، وأوافق على أن كل توقيع يصدر مني عبر هذا التطبيق يُعدّ ملزِماً لي قانونياً ومرتبطاً بهويتي، ويقوم مقام توقيعي اليدوي في جميع الإقرارات والمستندات داخل المنصّة."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="consent-mode-draw" onClick={() => setMode("draw")}
              className={`flex items-center justify-center gap-1.5 rounded-xl border-2 py-2.5 text-sm font-semibold transition ${mode === "draw" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>
              <PenLine className="h-4 w-4" /> ارسم توقيعك
            </button>
            <button type="button" data-testid="consent-mode-type" onClick={() => setMode("type")}
              className={`flex items-center justify-center gap-1.5 rounded-xl border-2 py-2.5 text-sm font-semibold transition ${mode === "type" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>
              <Type className="h-4 w-4" /> اكتب اسمك
            </button>
          </div>

          {mode === "draw" ? (
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-slate-700">وقّع بالإصبع أو الماوس داخل المربّع</p>
              <SignaturePad ref={sigRef} height={170} />
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="consent-typed-name" className="text-sm font-semibold text-slate-700">اكتب اسمك الكامل (سيكون توقيعك المعتمد)</label>
              <input id="consent-typed-name" type="text" data-testid="consent-typed-input" value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                enterKeyHint="done" placeholder="مثال: محمد أحمد"
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base focus:border-blue-400 focus:outline-none" />
              <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-2">
                <p className="text-[11px] text-slate-400 mb-1 text-center">معاينة التوقيع</p>
                <canvas ref={typedCanvasRef} width={520} height={110} data-testid="consent-typed-canvas" className="w-full h-24" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white px-4 pt-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)" }}>
        <div className="mx-auto max-w-lg">
          <button onClick={submit} disabled={submitting} data-testid="consent-submit"
            className="w-full rounded-xl bg-blue-600 py-4 text-base font-bold text-white hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} أوافق وأعتمد توقيعي
          </button>
          <p className="text-center text-[11px] text-slate-400 mt-1.5">بالضغط على الزر فإنك توافق على التعهّد أعلاه ويُعتمد توقيعك إلكترونياً.</p>
        </div>
      </div>
    </div>
  );
};
